/**
 * @file     preferenceSync.ts
 * @readme   ./README.md
 * @purpose  Fire-and-forget POST to /api/dashboard/preferences. The endpoint is auth-gated; unauthenticated requests return 401 and we ignore them. Idempotent on the server (top-level shallow merge). Coalesces rapid back-to-back writes into a single request via a 250 ms debounce buffer.
 */

import type { DataPointValue } from './types';

const DEBOUNCE_MS = 250;

let pending: Record<string, DataPointValue> = {};
let timer: ReturnType<typeof setTimeout> | null = null;

const flush = async () => {
  timer = null;
  const patch = pending;
  pending = {};
  if (Object.keys(patch).length === 0) return;

  try {
    const { secureFetch } = await import('@/services/anti-scraping/secureFetch');
    await secureFetch('/api/dashboard/preferences', {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ patch }),
    });
  } catch {
    // Best-effort. Anonymous users get 401 (ignored). Network failures
    // are tolerable, useMergeVisitorProfile will sweep up later from
    // localStorage on next dashboard mount.
  }
};

/** Queue a single key/value to sync to the server. Coalesced with other queued writes. */
export const queuePreferenceSync = (key: string, value: DataPointValue): void => {
  if (typeof window === 'undefined') return;
  pending[key] = value;
  if (timer !== null) clearTimeout(timer);
  timer = setTimeout(() => {
    void flush();
  }, DEBOUNCE_MS);
};

/** Queue many key/value pairs at once. */
export const queuePreferenceSyncMany = (entries: Record<string, DataPointValue>): void => {
  if (typeof window === 'undefined') return;
  Object.assign(pending, entries);
  if (timer !== null) clearTimeout(timer);
  timer = setTimeout(() => {
    void flush();
  }, DEBOUNCE_MS);
};
